<template>
  <v-container fluid class="assets-container pa-4">
    <!-- HEADER -->
    <div class="d-flex align-center mb-4">
      <v-avatar size="48" color="primary" class="mr-3">
        <v-icon dark size="28">loyalty</v-icon>
      </v-avatar>
      <div>
        <h1 class="text-h5 font-weight-bold mb-0" style="color:#263238;line-height:1.2">Activos Fijos</h1>
        <span class="text-caption" style="color:#78909c">
          {{ page === 1 ? 'Gestión y administración de activos fijos' : (isNewAsset ? 'Registro de nuevo activo' : 'Editar activo existente') }}
        </span>
      </div>
      <v-spacer></v-spacer>
      <v-chip v-if="page === 1" label outlined color="primary" class="font-weight-medium">
        <v-icon left small>inventory_2</v-icon>
        {{ assets.length }} registros
      </v-chip>
    </div>

    <v-stepper flat v-model="page" class="transparent-stepper">
      <v-stepper-items>
        <!-- ============================== -->
        <!-- PÁGINA 1: LISTADO DE ACTIVOS   -->
        <!-- ============================== -->
        <v-stepper-content :step="1" class="pa-0">
          <!-- KPI CARDS -->
          <v-row dense class="mb-1">
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="kpi-card">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption" style="color:#78909c;letter-spacing:0.5px">TOTAL DE ACTIVOS</div>
                      <div class="text-h5 font-weight-bold mt-1" style="color:#263238">{{ kpi.total }}</div>
                    </div>
                    <v-avatar size="48" color="blue lighten-5">
                      <v-icon color="primary" size="26">inventory_2</v-icon>
                    </v-avatar>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="kpi-card">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption" style="color:#78909c;letter-spacing:0.5px">VALOR INICIAL</div>
                      <div class="text-h5 font-weight-bold mt-1" style="color:#263238">{{ currencyFormat(kpi.totalInitial) }}</div>
                    </div>
                    <v-avatar size="48" color="green lighten-5">
                      <v-icon color="success" size="26">savings</v-icon>
                    </v-avatar>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="kpi-card">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption" style="color:#78909c;letter-spacing:0.5px">VALOR EN LIBRO</div>
                      <div class="text-h5 font-weight-bold mt-1" style="color:#263238">{{ currencyFormat(kpi.totalBalance) }}</div>
                    </div>
                    <v-avatar size="48" color="orange lighten-5">
                      <v-icon color="orange darken-2" size="26">account_balance</v-icon>
                    </v-avatar>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="kpi-card">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption" style="color:#78909c;letter-spacing:0.5px">DEPRECIABLES</div>
                      <div class="text-h5 font-weight-bold mt-1" style="color:#263238">{{ kpi.depreciating }}</div>
                    </div>
                    <v-avatar size="48" color="deep-purple lighten-5">
                      <v-icon color="deep-purple" size="26">trending_down</v-icon>
                    </v-avatar>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- FILTROS -->
          <v-card outlined class="mb-3">
            <v-card-text class="py-3">
              <v-row dense align="center">
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="business_selected"
                    :items="$store.state.auth.bussines_units"
                    label="Unidad de negocio"
                    color="primary"
                    prepend-inner-icon="corporate_fare"
                    outlined dense hide-details
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="location_selected"
                    :items="$store.state.auth.locations.filter((element) => element.bus_id == business_selected)"
                    color="primary"
                    label="Location"
                    prepend-inner-icon="my_location"
                    outlined dense hide-details clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filters.department_id"
                    :items="itemsDepartment"
                    color="primary"
                    label="Departamento"
                    prepend-inner-icon="business"
                    outlined dense hide-details clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="filters.asset_code"
                    label="Código"
                    prepend-inner-icon="qr_code_2"
                    outlined dense hide-details
                    @keyup.enter="filterAssets()"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="1" class="text-right">
                  <v-btn color="primary" depressed block @click="filterAssets()" height="40">
                    <v-icon>search</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- TABLA -->
          <v-card outlined>
            <v-toolbar flat height="64" class="px-2">
              <v-icon color="primary" class="mr-2">list_alt</v-icon>
              <v-toolbar-title class="font-weight-bold">Listado de activos</v-toolbar-title>
              <v-chip small outlined class="ml-3">{{ assets.length }}</v-chip>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                prepend-inner-icon="search"
                placeholder="Buscar en la tabla..."
                single-line outlined hide-details dense
                style="max-width:280px"
                class="mr-3"
              ></v-text-field>
              <v-menu offset-y>
                <template v-slot:activator="{ attrs, on }">
                  <v-btn outlined color="warning" v-bind="attrs" v-on="on" class="mr-2">
                    <v-icon left>description</v-icon>
                    Reportes
                    <v-icon right>expand_more</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="depreciation_assets_pdf">
                    <v-list-item-icon><v-icon color="primary">picture_as_pdf</v-icon></v-list-item-icon>
                    <v-list-item-title>Análisis mensual de depreciación</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openDialogDepRun">
                    <v-list-item-icon><v-icon color="deep-purple">trending_down</v-icon></v-list-item-icon>
                    <v-list-item-title>Correr depreciación del mes</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportAssetsExcel">
                    <v-list-item-icon><v-icon color="success">table_view</v-icon></v-list-item-icon>
                    <v-list-item-title>Exportar a Excel</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-btn v-if="hasFeature('INV-082')" color="primary" depressed @click="openDialogAssets()">
                <v-icon left>add</v-icon>
                Nuevo activo
              </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
            <v-data-table
              :headers="headersAssets"
              :search="search"
              :items="assets"
              height="55vh"
              fixed-header
              class="assets-table"
              :footer-props="{ itemsPerPageOptions: [15, 30, 50, 100, -1], 'items-per-page-text': 'Filas' }"
            >
              <template v-slot:[`item.code`]="{ item }">
                <span class="font-weight-bold primary--text">{{ item.code }}</span>
              </template>
              <template v-slot:[`item.item_name`]="{ item }">
                <div class="py-1">
                  <div class="font-weight-medium">{{ item.item_name }}</div>
                  <div class="text-caption" style="color:#90a4ae" v-if="item.model || item.serial_number">
                    {{ [item.model, item.serial_number].filter(Boolean).join(' · ') }}
                  </div>
                </div>
              </template>
              <template v-slot:[`item.category`]="{ item }">
                <v-chip v-if="item.category" x-small label outlined>{{ item.category }}</v-chip>
              </template>
              <template v-slot:[`item.sub_category`]="{ item }">
                <span class="text-caption">{{ item.sub_category }}</span>
              </template>
              <template v-slot:[`item.initial_book_value`]="{ item }">
                <span class="text-no-wrap">{{ currencyFormat(item.initial_book_value) }}</span>
              </template>
              <template v-slot:[`item.balance`]="{ item }">
                <span class="text-no-wrap font-weight-medium success--text">{{ currencyFormat(item.balance) }}</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex flex-column" style="gap:2px">
                  <v-chip x-small :color="item.active ? 'success' : 'grey'" dark label>
                    {{ item.active ? 'Activo' : 'Inactivo' }}
                  </v-chip>
                  <v-tooltip top v-if="warrantyAlert(item)">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip x-small color="warning" dark label v-bind="attrs" v-on="on">
                        <v-icon x-small left>warning</v-icon>Garantía
                      </v-chip>
                    </template>
                    <span>Garantía vence el {{ fmtDate(item.warranty_expiration) }}</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="viewAsset(item)">
                      <v-icon small color="info">visibility</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver detalle</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-if="hasFeature('INV-082')" icon small v-bind="attrs" v-on="on" @click="editAsset(item)">
                      <v-icon small color="primary">edit</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>
              </template>
              <template v-slot:no-data>
                <div class="py-8 text-center">
                  <v-icon size="64" color="grey lighten-1">inventory_2</v-icon>
                  <div class="text-body-1 mt-2" style="color:#90a4ae">No hay activos registrados</div>
                  <v-btn v-if="hasFeature('INV-082')" color="primary" text @click="openDialogAssets()" class="mt-2">
                    <v-icon left>add</v-icon>Crear primer activo
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-stepper-content>
        <!-- ============================== -->
        <!-- PÁGINA 2: EDITOR DE ACTIVO     -->
        <!-- ============================== -->
        <v-stepper-content :step="2" class="pa-0">
          <!-- BARRA DE ACCIONES -->
          <v-card outlined class="mb-3">
            <v-toolbar flat height="64" class="px-2">
              <v-btn icon @click="page = 1"><v-icon>arrow_back</v-icon></v-btn>
              <v-toolbar-title class="font-weight-bold">
                {{ isNewAsset ? 'Nuevo activo' : `Editar activo${newAssets.code ? ': ' + newAssets.code : ''}` }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn outlined color="error" @click="page=1" class="mr-2">
                <v-icon left>close</v-icon>Cancelar
              </v-btn>
              <v-btn color="success" depressed @click="saveAsset()" :loading="loading_save">
                <v-icon left>save</v-icon>Guardar
              </v-btn>
            </v-toolbar>
          </v-card>

          <v-row>
            <!-- IZQUIERDA: FORMULARIO CON TABS -->
            <v-col cols="12" md="8">
              <v-card outlined>
                <v-tabs v-model="editTab" color="primary" grow show-arrows>
                  <v-tab><v-icon left small>info</v-icon>General</v-tab>
                  <v-tab><v-icon left small>paid</v-icon>Financiero</v-tab>
                  <v-tab><v-icon left small>place</v-icon>Asignación</v-tab>
                  <v-tab><v-icon left small>notes</v-icon>Notas</v-tab>
                </v-tabs>
                <v-divider></v-divider>
                <v-tabs-items v-model="editTab">
                  <!-- TAB 1: GENERAL -->
                  <v-tab-item>
                    <v-card-text>
                      <div class="section-label">IDENTIFICACIÓN</div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="newAssets.code" label="Código" outlined dense hide-details prepend-inner-icon="qr_code_2"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="8">
                          <v-text-field
                            v-model="newAssets.item_name"
                            label="Descripción del activo"
                            readonly
                            v-on:keyup.enter="dialogItem = true"
                            outlined dense hide-details
                            prepend-inner-icon="inventory_2"
                            append-icon="search"
                            @click:append="dialogItem = true"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field v-model="newAssets.model" label="Modelo" outlined dense hide-details prepend-inner-icon="devices"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field v-model="newAssets.serial_number" label="Serial" outlined dense hide-details prepend-inner-icon="pin"></v-text-field>
                        </v-col>
                      </v-row>
                      <v-divider class="my-4"></v-divider>
                      <div class="section-label">CLASIFICACIÓN</div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.type_id" :items="asset_types" item-value="id" item-text="name" label="Tipo" outlined dense hide-details prepend-inner-icon="category"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.category_id" :items="categories" item-value="id" item-text="name" label="Categoría" outlined dense hide-details prepend-inner-icon="folder"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.sub_category_id" :items="sub_categories.filter((element) => element.category_id == newAssets.category_id)" item-value="id" item-text="name" label="Sub categoría" outlined dense hide-details prepend-inner-icon="folder_open"></v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-divider class="my-4"></v-divider>
                      <div class="section-label">ESTADO DEL ACTIVO</div>
                      <v-row>
                        <v-col cols="6" md="3">
                          <v-switch v-model="newAssets.is_internal" label="Es interno" color="primary" hide-details dense class="mt-0"></v-switch>
                        </v-col>
                        <v-col cols="6" md="3">
                          <v-switch v-model="newAssets.is_rented" label="Es rentado" color="primary" hide-details dense class="mt-0"></v-switch>
                        </v-col>
                        <v-col cols="6" md="3">
                          <v-switch v-model="newAssets.is_new" label="Es nuevo" color="primary" hide-details dense class="mt-0"></v-switch>
                        </v-col>
                        <v-col cols="6" md="3">
                          <v-switch v-model="newAssets.is_depreciable" label="Se deprecia" color="primary" hide-details dense class="mt-0"></v-switch>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-tab-item>

                  <!-- TAB 2: FINANCIERO -->
                  <v-tab-item>
                    <v-card-text>
                      <div class="section-label">INFORMACIÓN DE COMPRA</div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <FindInvoice v-if="page==2" @set-invoice="set_invoice" @clear="clear_invoice" />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-menu :v-model="menuDatePurchase == true" close-on-content-click :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field dense v-model="datePurchaseDMY" label="Fecha de compra" readonly hide-details v-bind="attrs" v-on="on" outlined prepend-inner-icon="event"></v-text-field>
                            </template>
                            <v-date-picker v-model="newAssets.date_of_purchase"></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="newAssets.amount" label="Precio de compra" outlined dense hide-details prepend-inner-icon="attach_money"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-menu v-model="menu_warranty" :close-on-content-click="false" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="newAssets.warranty_expiration"
                                label="Garantía vence"
                                readonly outlined dense hide-details clearable
                                prepend-inner-icon="verified"
                                v-bind="attrs" v-on="on"
                              />
                            </template>
                            <v-date-picker v-model="newAssets.warranty_expiration" @input="menu_warranty=false"/>
                          </v-menu>
                        </v-col>
                      </v-row>
                      <v-divider class="my-4"></v-divider>
                      <div class="section-label">DEPRECIACIÓN Y VALORES</div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="newAssets.initial_book_value" label="Valor en libro inicial" outlined dense hide-details type="number" min="1" prepend-inner-icon="account_balance"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="newAssets.salvage_value" label="Valor de salvamento" outlined dense hide-details prepend-inner-icon="savings"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="newAssets.useful_life" label="Vida útil (años)" outlined dense hide-details type="number" min="1" :disabled="!newAssets.is_depreciable" prepend-inner-icon="hourglass_top"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete v-model="newAssets.depreciation_tipe_id" :items="[{ value: 1, text: 'Linea recta' }]" label="Tipo de depreciación" outlined dense hide-details :disabled="!newAssets.is_depreciable" prepend-inner-icon="trending_down"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-menu ref="menu_effevite_date" v-model="menu_effevite_date" :close-on-content-click="false" :return-value.sync="newAssets.effective_date" transition="scale-transition" offset-y :disabled="!newAssets.is_depreciable" min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field readonly v-model="date_format_dmy" label="Fecha inicio depreciación" prepend-inner-icon="today" v-bind="attrs" v-on="on" hide-details dense :disabled="!newAssets.is_depreciable" outlined></v-text-field>
                            </template>
                            <v-date-picker v-model="newAssets.effective_date" no-title scrollable>
                              <v-spacer></v-spacer>
                              <v-btn text color="error" @click="menu_effevite_date = false">Cancelar</v-btn>
                              <v-btn text color="primary" @click="$refs.menu_effevite_date.save(newAssets.effective_date)">OK</v-btn>
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-tab-item>

                  <!-- TAB 3: ASIGNACIÓN -->
                  <v-tab-item>
                    <v-card-text>
                      <div class="section-label">UBICACIÓN</div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.business_unit_id" :items="$store.state.auth.bussines_units" label="Unidad de negocios" outlined dense hide-details prepend-inner-icon="corporate_fare"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.location_id" :items="$store.state.auth.locations.filter((element) => element.bus_id == newAssets.business_unit_id)" label="Location" outlined dense hide-details prepend-inner-icon="my_location"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-autocomplete v-model="newAssets.department_id" :items="itemsDepartment" label="Departamento" outlined dense hide-details prepend-inner-icon="business"></v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-divider class="my-4"></v-divider>
                      <div class="section-label">RESPONSABLES</div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <FindEmployee
                            v-if="page==2"
                            :label_description="'Responsable'"
                            :hide_details="true"
                            :default_input="newAssets.entity_name"
                            @set-employee="set_employee"
                            @clear="clear_employee"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <FindUser v-if="page==2" :default_input="newAssets.assigned_user" @set-user="set_user" @clear="clear_user" />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-tab-item>

                  <!-- TAB 4: NOTAS -->
                  <v-tab-item>
                    <v-card-text>
                      <div class="section-label">OBSERVACIONES</div>
                      <v-textarea v-model="newAssets.note" label="Notas y comentarios" outlined rows="8" hide-details auto-grow placeholder="Información adicional del activo..."></v-textarea>
                    </v-card-text>
                  </v-tab-item>
                </v-tabs-items>
              </v-card>
            </v-col>

            <!-- DERECHA: IMAGEN + METADATA -->
            <v-col cols="12" md="4">
              <v-card outlined class="mb-3">
                <v-card-title class="text-subtitle-2 font-weight-bold py-2">
                  <v-icon left small color="primary">image</v-icon>
                  Imagen del activo
                </v-card-title>
                <v-divider></v-divider>
                <v-hover v-slot="{ hover }">
                  <div>
                    <v-img contain :src="assetsImage" height="260" class="grey lighten-4">
                      <v-container fill-height fluid>
                        <v-row align="center" justify="center">
                          <v-col cols="12" align="center">
                            <v-file-input
                              v-show="assetsImage == null ? true : hover"
                              v-model="url_image"
                              @change="takeImage"
                              name="upload"
                              id="fileInput"
                              accept="image/png, image/jpeg, image/jpg"
                              :prepend-icon="assetsImage == null ? 'add_a_photo' : 'cameraswitch'"
                              hide-details hide-input hide-spin-buttons
                            ></v-file-input>
                            <div v-if="assetsImage == null" class="text-caption mt-2" style="color:#90a4ae">
                              Click para subir imagen
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-img>
                  </div>
                </v-hover>
              </v-card>

              <!-- RESUMEN FINANCIERO -->
              <v-card outlined class="mb-3" v-if="newAssets.amount || newAssets.initial_book_value">
                <v-card-title class="text-subtitle-2 font-weight-bold py-2">
                  <v-icon left small color="success">assessment</v-icon>
                  Resumen financiero
                </v-card-title>
                <v-divider></v-divider>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Precio de compra</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold">{{ currencyFormat(newAssets.amount || 0) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Valor en libro inicial</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold success--text">{{ currencyFormat(newAssets.initial_book_value || 0) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="newAssets.is_depreciable && newAssets.useful_life">
                    <v-list-item-content>
                      <v-list-item-subtitle>Depreciación anual estimada</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold orange--text">
                        {{ currencyFormat(((newAssets.initial_book_value || 0) - (newAssets.salvage_value || 0)) / (newAssets.useful_life || 1)) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>

              <!-- INFO DE REGISTRO -->
              <v-card outlined>
                <v-card-title class="text-subtitle-2 font-weight-bold py-2">
                  <v-icon left small color="grey darken-1">receipt_long</v-icon>
                  Información de registro
                </v-card-title>
                <v-divider></v-divider>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon class="mr-3"><v-icon small>person</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Creado por</v-list-item-subtitle>
                      <v-list-item-title class="text-body-2">{{ computed_created_by }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon class="mr-3"><v-icon small>event</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Fecha de creación</v-list-item-subtitle>
                      <v-list-item-title class="text-body-2">{{ computed_create_date }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <!-- DIÁLOGO: SELECCIÓN DE ARTÍCULO -->
    <v-dialog v-model="dialogItem" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text py-3">
          <v-icon dark class="mr-2">search</v-icon>
          <span class="text-h6 font-weight-bold">Seleccionar artículo</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogItem = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row justify="center">
            <ArticleFilters
              v-if="page==2"
              @getItemToEdit="addItemSelected"
              :dial="false"
              icon="done"
              name="Agregar"
              :searchWithLike="true"
            />
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="dialogItem = false">
            <v-icon left>close</v-icon>Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: VISOR DE ACTIVO       -->
    <!-- ============================== -->
    <v-dialog v-model="dialogView" max-width="1100" scrollable>
      <v-card class="view-dialog">
        <!-- HEADER -->
        <div class="view-header pa-4 d-flex align-center">
          <v-avatar size="48" color="rgba(255,255,255,0.2)" class="mr-3">
            <v-icon dark size="28">loyalty</v-icon>
          </v-avatar>
          <div class="white--text">
            <div class="text-caption" style="opacity:0.85;letter-spacing:0.6px">ACTIVO FIJO</div>
            <div class="text-h6 font-weight-bold" style="line-height:1.2">
              {{ viewAssetData.code }} · {{ viewAssetData.item_name }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon dark v-bind="attrs" v-on="on" @click="printAssetLabel" class="mr-1">
                <v-icon>qr_code_2</v-icon>
              </v-btn>
            </template>
            <span>Imprimir etiqueta QR</span>
          </v-tooltip>
          <v-switch
            v-model="viewAssetData.active"
            @change="toggleActive"
            color="white"
            hide-details dense dark
            class="mt-0 mr-2"
            :label="viewAssetData.active ? 'Activo' : 'Inactivo'"
            :loading="loadingAction"
          />
          <v-btn icon dark @click="dialogView = false"><v-icon>close</v-icon></v-btn>
        </div>

        <v-card-text class="pa-0" style="max-height:75vh">
          <v-row no-gutters>
            <!-- IZQUIERDA: IMAGEN + KPIs -->
            <v-col cols="12" md="4" class="pa-4" style="background:#fafbfc">
              <v-card outlined class="mb-3">
                <v-img
                  contain
                  :src="viewAssetImage"
                  height="260"
                  class="grey lighten-4"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-icon size="80" color="grey lighten-2">image_not_supported</v-icon>
                    </v-row>
                  </template>
                </v-img>
              </v-card>

              <!-- MINI KPIs -->
              <v-card outlined class="mb-3">
                <v-list dense class="py-0">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-caption">Precio de compra</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold">{{ currencyFormat(viewAssetData.amount || 0) }}</v-list-item-title>
                    </v-list-item-content>
                    <v-icon color="primary">attach_money</v-icon>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-caption">Valor inicial</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold">{{ currencyFormat(viewAssetData.initial_book_value || 0) }}</v-list-item-title>
                    </v-list-item-content>
                    <v-icon color="success">savings</v-icon>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-caption">Valor actual (libro)</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold success--text">{{ currencyFormat(viewAssetData.balance || 0) }}</v-list-item-title>
                    </v-list-item-content>
                    <v-icon color="success">account_balance</v-icon>
                  </v-list-item>
                  <v-divider v-if="viewAssetData.is_depreciable"></v-divider>
                  <v-list-item v-if="viewAssetData.is_depreciable">
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-caption">Depreciación anual estimada</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold orange--text">
                        {{ currencyFormat(depreciationPreview(viewAssetData)) }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-icon color="orange darken-2">trending_down</v-icon>
                  </v-list-item>
                </v-list>
              </v-card>

              <!-- ESTADO / BANDERAS -->
              <v-card outlined>
                <v-card-title class="text-subtitle-2 font-weight-bold py-2">
                  <v-icon left small>flag</v-icon>Propiedades
                </v-card-title>
                <v-divider></v-divider>
                <div class="pa-3 d-flex flex-wrap" style="gap:6px">
                  <v-chip x-small label :color="viewAssetData.is_new ? 'success' : 'grey lighten-2'" :dark="!!viewAssetData.is_new">
                    <v-icon x-small left>{{ viewAssetData.is_new ? 'check' : 'close' }}</v-icon>Nuevo
                  </v-chip>
                  <v-chip x-small label :color="viewAssetData.is_internal ? 'primary' : 'grey lighten-2'" :dark="!!viewAssetData.is_internal">
                    <v-icon x-small left>{{ viewAssetData.is_internal ? 'check' : 'close' }}</v-icon>Interno
                  </v-chip>
                  <v-chip x-small label :color="viewAssetData.is_rented ? 'warning' : 'grey lighten-2'" :dark="!!viewAssetData.is_rented">
                    <v-icon x-small left>{{ viewAssetData.is_rented ? 'check' : 'close' }}</v-icon>Rentado
                  </v-chip>
                  <v-chip x-small label :color="viewAssetData.is_depreciable ? 'deep-purple' : 'grey lighten-2'" :dark="!!viewAssetData.is_depreciable">
                    <v-icon x-small left>{{ viewAssetData.is_depreciable ? 'check' : 'close' }}</v-icon>Depreciable
                  </v-chip>
                </div>
              </v-card>
            </v-col>

            <!-- DERECHA: DETALLES -->
            <v-col cols="12" md="8" class="pa-4">
              <!-- IDENTIFICACIÓN -->
              <div class="section-label">IDENTIFICACIÓN</div>
              <v-row dense class="mb-3">
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Código</div><div class="info-value font-weight-bold">{{ viewAssetData.code || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Modelo</div><div class="info-value">{{ viewAssetData.model || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Serial</div><div class="info-value">{{ viewAssetData.serial_number || '—' }}</div></div></v-col>
                <v-col cols="12"><div class="info-field"><div class="info-label">Descripción</div><div class="info-value">{{ viewAssetData.item_name || '—' }}</div></div></v-col>
              </v-row>

              <!-- CLASIFICACIÓN -->
              <div class="section-label">CLASIFICACIÓN</div>
              <v-row dense class="mb-3">
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Tipo</div><div class="info-value">{{ viewAssetData.type || viewAssetData.type_name || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Categoría</div><div class="info-value">{{ viewAssetData.category || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Sub categoría</div><div class="info-value">{{ viewAssetData.sub_category || '—' }}</div></div></v-col>
              </v-row>

              <!-- FINANCIERO -->
              <div class="section-label">FINANCIERO</div>
              <v-row dense class="mb-3">
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Fecha de compra</div><div class="info-value">{{ fmtDate(viewAssetData.date_of_purchase) }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Precio de compra</div><div class="info-value font-weight-bold">{{ currencyFormat(viewAssetData.amount || 0) }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Valor de salvamento</div><div class="info-value">{{ currencyFormat(viewAssetData.salvage_value || 0) }}</div></div></v-col>
                <v-col cols="6" md="4" v-if="viewAssetData.is_depreciable"><div class="info-field"><div class="info-label">Vida útil</div><div class="info-value">{{ viewAssetData.useful_life || '—' }} años</div></div></v-col>
                <v-col cols="6" md="4" v-if="viewAssetData.is_depreciable"><div class="info-field"><div class="info-label">Inicio depreciación</div><div class="info-value">{{ fmtDate(viewAssetData.effective_date) }}</div></div></v-col>
                <v-col cols="6" md="4" v-if="viewAssetData.is_depreciable"><div class="info-field"><div class="info-label">Tipo de depreciación</div><div class="info-value">Línea recta</div></div></v-col>
              </v-row>

              <!-- UBICACIÓN -->
              <div class="section-label">UBICACIÓN Y ASIGNACIÓN</div>
              <v-row dense class="mb-3">
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Unidad de negocio</div><div class="info-value">{{ viewAssetData.business_unit_name || viewAssetData.business || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Location</div><div class="info-value">{{ viewAssetData.location || viewAssetData.location_name || '—' }}</div></div></v-col>
                <v-col cols="6" md="4"><div class="info-field"><div class="info-label">Departamento</div><div class="info-value">{{ viewAssetData.department_name || viewAssetData.department || '—' }}</div></div></v-col>
                <v-col cols="6" md="6"><div class="info-field"><div class="info-label">Responsable</div><div class="info-value"><v-icon x-small class="mr-1">person</v-icon>{{ viewAssetData.entity_name || '—' }}</div></div></v-col>
                <v-col cols="6" md="6"><div class="info-field"><div class="info-label">Usuario asignado</div><div class="info-value"><v-icon x-small class="mr-1">account_circle</v-icon>{{ viewAssetData.assigned_user || '—' }}</div></div></v-col>
              </v-row>

              <!-- NOTAS -->
              <div class="section-label">NOTAS</div>
              <v-card outlined class="mb-3 pa-3" style="background:#fafbfc;min-height:60px">
                <div v-if="viewAssetData.note" class="text-body-2" style="white-space:pre-wrap">{{ viewAssetData.note }}</div>
                <div v-else class="text-caption" style="color:#90a4ae">Sin notas adicionales</div>
              </v-card>

              <!-- REGISTRO -->
              <div class="section-label">REGISTRO</div>
              <v-row dense class="mb-3">
                <v-col cols="6"><div class="info-field"><div class="info-label">Creado por</div><div class="info-value"><v-icon x-small class="mr-1">person</v-icon>{{ viewAssetData.created_by || '—' }}</div></div></v-col>
                <v-col cols="6"><div class="info-field"><div class="info-label">Fecha de creación</div><div class="info-value"><v-icon x-small class="mr-1">event</v-icon>{{ fmtDate(viewAssetData.created_date) }}</div></div></v-col>
              </v-row>

              <!-- TIMELINE HISTORIAL -->
              <v-expansion-panels flat multiple class="history-panels">
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-1 px-2">
                    <div class="d-flex align-center">
                      <v-icon small left color="deep-purple">trending_down</v-icon>
                      <span class="text-body-2 font-weight-bold">Depreciación</span>
                      <v-chip x-small class="ml-2">{{ history.depreciation.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!history.depreciation.length" class="text-caption" style="color:#90a4ae">Sin movimientos de depreciación</div>
                    <v-simple-table v-else dense>
                      <thead>
                        <tr>
                          <th>Periodo</th><th class="text-right">Depreciación</th>
                          <th class="text-right">Acum.</th><th class="text-right">Val. libro</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="h in history.depreciation" :key="h.id">
                          <td>{{ h.period_year }}-{{ String(h.period_month).padStart(2,'0') }}</td>
                          <td class="text-right">{{ currencyFormat(h.depreciation_amount) }}</td>
                          <td class="text-right">{{ currencyFormat(h.accumulated_after) }}</td>
                          <td class="text-right">{{ currencyFormat(h.book_value_after) }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="py-1 px-2">
                    <div class="d-flex align-center">
                      <v-icon small left color="primary">swap_horiz</v-icon>
                      <span class="text-body-2 font-weight-bold">Transferencias</span>
                      <v-chip x-small class="ml-2">{{ history.transfers.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!history.transfers.length" class="text-caption" style="color:#90a4ae">Sin transferencias</div>
                    <v-timeline dense v-else>
                      <v-timeline-item v-for="t in history.transfers" :key="t.id" small color="primary">
                        <div class="text-caption" style="color:#90a4ae">{{ fmtDate(t.transfer_date) }}</div>
                        <div class="text-body-2">
                          <b>{{ t.from_location_name || '—' }}</b> → <b>{{ t.to_location_name || '—' }}</b>
                        </div>
                        <div v-if="t.reason" class="text-caption">{{ t.reason }}</div>
                        <div class="text-caption" style="color:#90a4ae">Por: {{ t.created_by_name }}</div>
                      </v-timeline-item>
                    </v-timeline>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="py-1 px-2">
                    <div class="d-flex align-center">
                      <v-icon small left color="orange darken-2">build</v-icon>
                      <span class="text-body-2 font-weight-bold">Mantenimientos</span>
                      <v-chip x-small class="ml-2">{{ history.maintenance.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!history.maintenance.length" class="text-caption" style="color:#90a4ae">Sin mantenimientos</div>
                    <v-timeline dense v-else>
                      <v-timeline-item v-for="m in history.maintenance" :key="m.id" small :color="m.maintenance_type === 'preventive' ? 'success' : 'warning'">
                        <div class="text-caption" style="color:#90a4ae">{{ fmtDate(m.maintenance_date) }}</div>
                        <div class="text-body-2 font-weight-medium">
                          {{ m.maintenance_type === 'preventive' ? 'Preventivo' : 'Correctivo' }} · {{ currencyFormat(m.cost) }}
                        </div>
                        <div class="text-caption">{{ m.description }}</div>
                        <div v-if="m.provider_name" class="text-caption" style="color:#90a4ae">
                          Proveedor: {{ m.provider_name }}
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="py-1 px-2">
                    <div class="d-flex align-center">
                      <v-icon small left color="info">attach_file</v-icon>
                      <span class="text-body-2 font-weight-bold">Adjuntos</span>
                      <v-chip x-small class="ml-2">{{ history.attachments.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!history.attachments.length" class="text-caption" style="color:#90a4ae">Sin adjuntos</div>
                    <v-list dense v-else>
                      <v-list-item v-for="a in history.attachments" :key="a.id">
                        <v-list-item-icon><v-icon small>{{ attachmentIcon(a.file_type) }}</v-icon></v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">{{ a.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ a.file_type || 'otro' }} · {{ fmtDate(a.created_date) }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action class="d-flex flex-row">
                          <v-btn icon small :href="assetImageUrl(a.url)" target="_blank"><v-icon small>download</v-icon></v-btn>
                          <v-btn icon small @click="deleteAttachment(a.id)"><v-icon small color="error">delete</v-icon></v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel v-if="history.disposals.length">
                  <v-expansion-panel-header class="py-1 px-2">
                    <div class="d-flex align-center">
                      <v-icon small left color="error">block</v-icon>
                      <span class="text-body-2 font-weight-bold">Bajas / Retiros</span>
                      <v-chip x-small class="ml-2" color="error" dark>{{ history.disposals.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list dense>
                      <v-list-item v-for="d in history.disposals" :key="d.id">
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-chip x-small label color="error" dark>{{ disposalLabel(d.disposal_type) }}</v-chip>
                            {{ fmtDate(d.disposal_date) }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            Val. libro: {{ currencyFormat(d.book_value_at_disposal) }} ·
                            Recuperado: {{ currencyFormat(d.disposal_amount) }} ·
                            <span :class="d.gain_loss >= 0 ? 'success--text' : 'error--text'">
                              {{ d.gain_loss >= 0 ? 'Ganancia' : 'Pérdida' }}: {{ currencyFormat(Math.abs(d.gain_loss)) }}
                            </span>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle v-if="d.reason">{{ d.reason }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-4 py-3">
          <v-btn text @click="dialogView = false">
            <v-icon left>close</v-icon>Cerrar
          </v-btn>
          <v-spacer></v-spacer>
          <!-- Acciones ERP -->
          <v-menu offset-y v-if="viewAssetData.active">
            <template v-slot:activator="{ attrs, on }">
              <v-btn outlined color="grey darken-1" v-bind="attrs" v-on="on" class="mr-2">
                <v-icon left>more_vert</v-icon>Acciones
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openDialogTransfer">
                <v-list-item-icon><v-icon color="primary">swap_horiz</v-icon></v-list-item-icon>
                <v-list-item-title>Transferir</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openDialogMaintenance">
                <v-list-item-icon><v-icon color="orange darken-2">build</v-icon></v-list-item-icon>
                <v-list-item-title>Registrar mantenimiento</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openDialogAttachment">
                <v-list-item-icon><v-icon color="info">attach_file</v-icon></v-list-item-icon>
                <v-list-item-title>Subir adjunto</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="openDialogDisposal">
                <v-list-item-icon><v-icon color="error">block</v-icon></v-list-item-icon>
                <v-list-item-title>Dar de baja</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn color="primary" depressed @click="goEditFromView">
            <v-icon left>edit</v-icon>Editar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: DAR DE BAJA           -->
    <!-- ============================== -->
    <v-dialog v-model="dialogDisposal" max-width="600" persistent>
      <v-card>
        <v-card-title class="error white--text py-3">
          <v-icon dark class="mr-2">block</v-icon>
          <span class="text-h6">Dar de baja — {{ viewAssetData.code }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogDisposal=false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="disposalForm.disposal_type"
                :items="[
                  {text:'Venta',value:'sale'},
                  {text:'Donación',value:'donation'},
                  {text:'Obsolescencia',value:'obsolete'},
                  {text:'Pérdida',value:'loss'},
                  {text:'Robo',value:'theft'}
                ]"
                label="Motivo de baja"
                outlined dense hide-details
                prepend-inner-icon="category"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-menu v-model="disposalForm.dateMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{on,attrs}">
                  <v-text-field
                    v-model="disposalForm.disposal_date"
                    label="Fecha de baja"
                    readonly outlined dense hide-details
                    prepend-inner-icon="today"
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker v-model="disposalForm.disposal_date" @input="disposalForm.dateMenu=false"/>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="disposalForm.disposal_amount"
                label="Monto recuperado (venta/seguro)"
                type="number" outlined dense hide-details
                prepend-inner-icon="attach_money"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="disposalForm.reason"
                label="Motivo / justificación" outlined dense rows="3" hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-alert dense outlined type="info" class="mb-0">
                <div class="text-caption">Valor en libro actual: <b>{{ currencyFormat(viewAssetData.balance) }}</b></div>
                <div class="text-caption">Al confirmar, el activo quedará marcado como <b>inactivo</b> con balance 0.</div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogDisposal=false">Cancelar</v-btn>
          <v-btn color="error" depressed @click="submitDisposal" :loading="loadingAction">
            <v-icon left>block</v-icon>Confirmar baja
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: TRANSFERIR            -->
    <!-- ============================== -->
    <v-dialog v-model="dialogTransfer" max-width="700" persistent>
      <v-card>
        <v-card-title class="primary white--text py-3">
          <v-icon dark class="mr-2">swap_horiz</v-icon>
          <span class="text-h6">Transferir — {{ viewAssetData.code }}</span>
          <v-spacer/>
          <v-btn icon dark @click="dialogTransfer=false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert dense outlined type="info" class="mb-3 text-caption">
            Actual: {{ viewAssetData.business_unit_name || '—' }} · {{ viewAssetData.location_name || '—' }} · {{ viewAssetData.department_name || '—' }}
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-menu v-model="transferForm.dateMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{on,attrs}">
                  <v-text-field v-model="transferForm.transfer_date" label="Fecha" readonly outlined dense hide-details prepend-inner-icon="today" v-bind="attrs" v-on="on"/>
                </template>
                <v-date-picker v-model="transferForm.transfer_date" @input="transferForm.dateMenu=false"/>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="transferForm.to_business_unit_id" :items="$store.state.auth.bussines_units" label="Nueva unidad de negocio" outlined dense hide-details prepend-inner-icon="corporate_fare"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="transferForm.to_location_id" :items="$store.state.auth.locations.filter(e => e.bus_id == transferForm.to_business_unit_id)" label="Nueva location" outlined dense hide-details prepend-inner-icon="my_location"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="transferForm.to_department_id" :items="itemsDepartment" label="Nuevo departamento" outlined dense hide-details prepend-inner-icon="business"/>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="transferForm.reason" label="Motivo de la transferencia" outlined dense rows="2" hide-details/>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogTransfer=false">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="submitTransfer" :loading="loadingAction">
            <v-icon left>swap_horiz</v-icon>Transferir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: MANTENIMIENTO         -->
    <!-- ============================== -->
    <v-dialog v-model="dialogMaintenance" max-width="700" persistent>
      <v-card>
        <v-card-title class="orange darken-2 white--text py-3">
          <v-icon dark class="mr-2">build</v-icon>
          <span class="text-h6">Mantenimiento — {{ viewAssetData.code }}</span>
          <v-spacer/>
          <v-btn icon dark @click="dialogMaintenance=false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="maintenanceForm.maintenance_type" :items="[{text:'Preventivo',value:'preventive'},{text:'Correctivo',value:'corrective'}]" label="Tipo" outlined dense hide-details prepend-inner-icon="category"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu v-model="maintenanceForm.dateMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{on,attrs}">
                  <v-text-field v-model="maintenanceForm.maintenance_date" label="Fecha" readonly outlined dense hide-details prepend-inner-icon="today" v-bind="attrs" v-on="on"/>
                </template>
                <v-date-picker v-model="maintenanceForm.maintenance_date" @input="maintenanceForm.dateMenu=false"/>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="maintenanceForm.description" label="Descripción" outlined dense rows="3" hide-details/>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="maintenanceForm.provider_name" label="Proveedor" outlined dense hide-details prepend-inner-icon="store"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="maintenanceForm.cost" label="Costo" type="number" outlined dense hide-details prepend-inner-icon="attach_money"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu v-model="maintenanceForm.nextMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{on,attrs}">
                  <v-text-field v-model="maintenanceForm.next_maintenance_date" label="Próximo mantenimiento" readonly outlined dense hide-details prepend-inner-icon="event_repeat" v-bind="attrs" v-on="on" clearable/>
                </template>
                <v-date-picker v-model="maintenanceForm.next_maintenance_date" @input="maintenanceForm.nextMenu=false"/>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogMaintenance=false">Cancelar</v-btn>
          <v-btn color="orange darken-2" depressed dark @click="submitMaintenance" :loading="loadingAction">
            <v-icon left>save</v-icon>Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: ADJUNTO               -->
    <!-- ============================== -->
    <v-dialog v-model="dialogAttachment" max-width="600" persistent>
      <v-card>
        <v-card-title class="info white--text py-3">
          <v-icon dark class="mr-2">attach_file</v-icon>
          <span class="text-h6">Adjuntar documento — {{ viewAssetData.code }}</span>
          <v-spacer/>
          <v-btn icon dark @click="dialogAttachment=false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="attachmentForm.name" label="Nombre" outlined dense hide-details prepend-inner-icon="title"/>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="attachmentForm.file_type" :items="['factura','garantia','manual','poliza','foto','otro']" label="Tipo" outlined dense hide-details prepend-inner-icon="category"/>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="attachmentForm.description" label="Descripción" outlined dense rows="2" hide-details/>
            </v-col>
            <v-col cols="12">
              <v-file-input v-model="attachmentForm.file" label="Archivo" outlined dense hide-details show-size prepend-icon="" prepend-inner-icon="upload_file"/>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogAttachment=false">Cancelar</v-btn>
          <v-btn color="info" depressed @click="submitAttachment" :loading="loadingAction">
            <v-icon left>cloud_upload</v-icon>Subir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================== -->
    <!-- DIÁLOGO: CORRER DEPRECIACIÓN   -->
    <!-- ============================== -->
    <v-dialog v-model="dialogDepRun" max-width="500" persistent>
      <v-card>
        <v-card-title class="deep-purple white--text py-3">
          <v-icon dark class="mr-2">trending_down</v-icon>
          <span class="text-h6">Correr depreciación mensual</span>
          <v-spacer/>
          <v-btn icon dark @click="dialogDepRun=false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="6">
              <v-select v-model="depRunForm.year" :items="depYears" label="Año" outlined dense hide-details/>
            </v-col>
            <v-col cols="6">
              <v-select v-model="depRunForm.month" :items="depMonths" label="Mes" outlined dense hide-details/>
            </v-col>
            <v-col cols="12">
              <v-alert dense outlined type="warning" class="mb-0 text-caption">
                Se depreciarán todos los activos activos y depreciables de la unidad de negocio seleccionada que aún no tengan registro en este periodo.
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogDepRun=false">Cancelar</v-btn>
          <v-btn color="deep-purple" dark depressed @click="runDepreciation" :loading="loadingAction">
            <v-icon left>play_arrow</v-icon>Ejecutar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import jsPDF from "jspdf";
import moment from "moment";
import ArticleFilters from "@/components/Inventory/ArticleSelectionList.vue";
import FindEmployee from "@/components/base/FindEmployee.vue";
import FindInvoice from "@/components/base/FindInvoice.vue";
import FindUser from "@/components/base/FindUser.vue";
export default {
  data: () => ({
    filters: {
      department_id: null,
      asset_code:null
    },
    loading_save:false,
    page: 1,
    editTab: 0,
    menu_effevite_date: false,
    menu_warranty: false,
    newAssets: {
      code: null,
      item_id: null,
      item_name: null,
      date_of_purchase: null,
      category_id: null,
      sub_category_id: null,
      entity_id: null,
      entity_name: null,
      invoice_id: null,
      business_unit_id: null,
      location_id: null,
      invoice_items:[],
      department_id: null,
      amount: null,
      useful_life: null,
      salvage_value: null,
      created_date: null,
      created_by: null,
      created_by_id: null,
      assigned_user_id:null,
      assigned_user: null,
      model: null,
      serial_number: null,
      image_url: null,
      note: null,
      active: true,
      type_id: null,
      is_new: true,
      is_rented: true,
      is_internal: true,
      is_depreciable: true,
      depreciation_tipe_id: null,
      effective_date: null,
      initial_book_value: null,
      warranty_expiration: null,
    },
    categories: [],
    sub_categories: [],
    asset_types: [],
    itemsDepartment: [],
    chartOfAccount: [],
    assets: [],
    search: null,
    assetsImage: null,
    headersAssets: [
      { text: "Código", align: "start", value: "code", width: 110 },
      { text: "Activo", value: "item_name" },
      { text: "Categoría", value: "category", width: 140 },
      { text: "Sub categoría", value: "sub_category", width: 140 },
      { text: "Valor inicial", value: "initial_book_value", align: "end", width: 140 },
      { text: "Valor actual", value: "balance", align: "end", width: 140 },
      { text: "Estado", value: "status", align: "center", width: 100, sortable: false },
      { text: "Acciones", value: "actions", align: "center", sortable: false, width: 120 },
    ],
    menuDatePurchase: false,
    dialogItem: false,
    url_image: null,
    changeImage: false,
    isNewAsset: false,
    dialogView: false,
    viewAssetData: {},
    viewAssetImage: null,
    // Historial dentro del visor
    history: {
      depreciation: [],
      transfers: [],
      maintenance: [],
      attachments: [],
      disposals: [],
    },
    // Dialog: dar de baja
    dialogDisposal: false,
    disposalForm: {
      disposal_type: 'sale',
      disposal_date: new Date().toISOString().substr(0, 10),
      disposal_amount: 0,
      reason: '',
      dateMenu: false,
    },
    // Dialog: transferir
    dialogTransfer: false,
    transferForm: {
      transfer_date: new Date().toISOString().substr(0, 10),
      to_business_unit_id: null,
      to_location_id: null,
      to_department_id: null,
      reason: '',
      dateMenu: false,
    },
    // Dialog: mantenimiento
    dialogMaintenance: false,
    maintenanceForm: {
      maintenance_type: 'preventive',
      maintenance_date: new Date().toISOString().substr(0, 10),
      description: '',
      provider_name: '',
      cost: 0,
      next_maintenance_date: null,
      dateMenu: false,
      nextMenu: false,
    },
    // Dialog: adjunto
    dialogAttachment: false,
    attachmentForm: {
      name: '',
      description: '',
      file_type: 'otro',
      file: null,
    },
    // Dialog: correr depreciación
    dialogDepRun: false,
    depRunForm: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    },
    loadingAction: false,
  }),
  components: {
    ArticleFilters,
    FindEmployee,
    FindInvoice,
    FindUser
  },
  mounted() {
    // Verificar si existe y remover la clase
    this.$el.closest(".page-wrapper")?.classList.remove("page-wrapper");
    this.getData();
  },
  beforeDestroy() {
    // Agregar la clase nuevamente al salir
    this.$el.closest(".page-wrapper")?.classList.add("page-wrapper");
  },
  computed: {
    business_selected: {
      get() {
        return this.$store.state.app.filters.business;
      },
      set(value) {
        this.$store.commit("setBusiness", value);
      },
    },
    location_selected: {
      get() {
        return this.$store.state.app.filters.location;
      },
      set(value) {
        this.$store.commit("setLocation", value);
      },
    },
    datePurchaseDMY() {
      if (this.newAssets.date_of_purchase == null) {
        return null;
      }
      return moment(this.newAssets.date_of_purchase,"YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY h:mm a");
    },
    date_format_dmy() {
      if (this.newAssets.effective_date == null) {
        return "ESCOJA UNA FECHA";
      }
      return moment(this.newAssets.effective_date).format("DD/MM/YYYY");
    },
    computed_created_by(){
      if(this.newAssets.created_by==null){
        return this.$store.state.auth.user.user.fullname;
      }
      return this.newAssets.created_by;
    },
    computed_create_date(){
      if(this.newAssets.created_date==null){
        return moment().format('DD/MM/YYYY');
      }
      return this.newAssets.created_date;
    },
    kpi(){
      const total = this.assets.length;
      const totalInitial = this.assets.reduce((s, a) => s + (parseFloat(a.initial_book_value) || 0), 0);
      const totalBalance = this.assets.reduce((s, a) => s + (parseFloat(a.balance) || 0), 0);
      const depreciating = this.assets.filter(a => a.is_depreciable).length;
      return { total, totalInitial, totalBalance, depreciating };
    },
    depYears(){
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
    depMonths(){
      return [
        { text: 'Enero', value: 1 }, { text: 'Febrero', value: 2 }, { text: 'Marzo', value: 3 },
        { text: 'Abril', value: 4 }, { text: 'Mayo', value: 5 }, { text: 'Junio', value: 6 },
        { text: 'Julio', value: 7 }, { text: 'Agosto', value: 8 }, { text: 'Septiembre', value: 9 },
        { text: 'Octubre', value: 10 }, { text: 'Noviembre', value: 11 }, { text: 'Diciembre', value: 12 },
      ];
    }
  },
  watch: {},
  methods: {
    async getData() {
      this.filterAssets();
      // this.filters.business = this.$store.state.auth.bussines_units[0].value;
      let resultTypeAccounts = await this.api_Get("accounting/accountsUsable");
      const dataTypeAccounts = await resultTypeAccounts.json();
      for (let item of dataTypeAccounts) {
        this.chartOfAccount.push({
          text: item.code + " - " + item.name,
          value: item.id,
          dgii_category_id: item.dgii_category_id,
        });
      }
      this.itemsDepartment = await this.g_departments();
      const categories_request = await this.api_Get(
        "inventory/asset_categories"
      );
      this.categories = await categories_request.json();

      const sub_categories_equest = await this.api_Get(
        "inventory/asset_sub_categories"
      );
      this.sub_categories = await sub_categories_equest.json();
      const type_request = await this.api_Get("inventory/asset_types");
      this.asset_types = await type_request.json();
    },
    async filterAssets() {
      if(this.location_selected?.value==0){
        this.location_selected=null;
      }
      const request = await this.api_Post("inventory/assetsFilter", {
        business_unit_id: this.business_selected,
        location_id:this.location_selected,
        department_id:this.filters.department_id,
        asset_code:this.clearInputFilter(this.filters.asset_code,0)
      });
      const data = await request.json();
      if (request.status == 200) {
        this.assets = data;
      } else {
        this.$toast.error(data.error);
      }
    },
    openDialogAssets() {
      this.isNewAsset = true;
      this.assetsImage = null;
      this.url_image = null;
      this.changeImage = false;
      this.editTab = 0;
      this.newAssets = {
        code: null,
        item_id: null,
        item_name: null,
        date_of_purchase: null,
        category: null,
        category_id: null,
        entity_id: null,
        entity_name: null,
        invoice_id: null,
        invoice_items:[],
        business_unit_id: null,
        location_id: null,
        department_id: null,
        amount: null,
        useful_life: null,
        salvage_value: null,
        created_date: null,
        created_by: null,
        created_by_id: null,
        assigned_user_id: null,
        assigned_user: null,
        model: null,
        serial_number: null,
        image_url: null,
        note: null,
        active: true,
        sub_category_id:null,
        initial_book_value:null,
        effective_date:null,
        is_depreciable:true,
        is_internal:true,
        is_new:true,
        is_rented:false,
        balance:0,
        type_id:null,
        depreciation_tipe_id:1,
        warranty_expiration:null
      };
      this.page = 2;
    },
    addItemSelected(item) {
      //SI YA CARGO LA FACTURA DE COMPRA, VERIFICA QUE EL ITEM SELECCIONADO SE ENCUENTRE EN DICHA FACTURA
      if(this.newAssets.invoice_items.length>0){
        const item_in_invoice =this.newAssets.invoice_items.find((element)=>element.item_id==item.item_id);
        if(item_in_invoice==undefined){
          this.$toast.error(`El item ${item.item_name} no se encuetra en la factutra seleccionada!`);
          return
        }
        this.newAssets.amount=item_in_invoice.price;
      }
      this.newAssets.item_name = item.item_name;
      this.newAssets.item_id = item.item_id;
      this.dialogItem = false;
    },
    takeImage() {
      if (this.url_image == null || this.url_image == undefined) {
        return;
      }
      this.assetsImage = URL.createObjectURL(this.url_image);
      this.changeImage = true;
    },
    async saveAsset() {
      if(this.newAssets.code==null){
        this.$toast.warning("Indique el codigo!");
        return
      }
      if(this.newAssets.item_id==null){
        this.$toast.warning("Escoja el activo!");
        return
      }
      if(this.newAssets.category_id==null){
        this.$toast.warning("Escoja una categoria!");
        return
      }
      if(this.newAssets.sub_category_id==null){
        this.$toast.warning("Escoja una Subcategoria!");
        return
      }
      if(this.newAssets.business_unit_id==null){
        this.$toast.warning("Indique la unidad de negocios!");
        return
      }
      if(this.newAssets.location_id==null){
        this.$toast.warning("Escoja una location!");
        return
      }
      if(this.newAssets.amount==null){
        this.$toast.warning("Indique el precio de compra!");
        return
      }
      if(this.newAssets.is_depreciable && this.newAssets.useful_life==null){
        this.$toast.warning("Indique el tiempo de vida util!");
        return
      }
      if(this.newAssets.is_depreciable && this.newAssets.effective_date==null){
        this.$toast.warning("Indique la fecha de inicio de depreciacion!");
        return
      }
      if(this.newAssets.salvage_value==null){
        this.$toast.warning("Indique el valor de salvamento!");
        return
      }
      if(this.assetsImage==null){
        this.$toast.warning("Establesca la imagen del activo!");
        return
      }
      this.loading_save=true;
      try {
        // if (this.newAssets.image_url != null && this.newAssets.image_url != "null") {
        //   await this.delete_file_storage("fixed_assets",this.newAssets.image_url);
        // }
        let newImage = { id: this.newAssets.image_url || null };
        if (this.changeImage == true && this.url_image) {
          try {
            const result = await this.upload_to_spaces(this.url_image, "assets");
            if (!result || !result.key) {
              this.$toast.error("Error al subir la imagen: respuesta vacía del servidor");
              this.loading_save = false;
              return;
            }
            newImage = { id: result.key };
            console.log("[assets] imagen subida a DO:", result);
          } catch (uploadErr) {
            console.error("[assets] upload_to_spaces error:", uploadErr);
            this.$toast.error("Error al subir imagen: " + uploadErr.message);
            this.loading_save = false;
            return;
          }
        }
        let assetData = {
            code: this.newAssets.code,
            item_id: this.newAssets.item_id,
            date_of_purchase: this.newAssets.date_of_purchase,
            category_id: this.newAssets.category_id,
            entity_id: this.newAssets.entity_id,
            invoice_id: this.newAssets.invoice_id,
            business_unit_id: this.newAssets.business_unit_id,
            location_id: this.newAssets.location_id,
            department_id: this.newAssets.department_id,
            amount: this.newAssets.amount,
            useful_life: this.newAssets.useful_life,
            salvage_value: this.newAssets.salvage_value,
            created_by: this.newAssets.created_by_id||this.$store.state.auth.user.user.user_id,
            assigned_user: this.newAssets.assigned_user_id,
            model: this.newAssets.model,
            serial_number: this.newAssets.serial_number,
            image_url: newImage.id,
            note: this.newAssets.note,
            active: this.newAssets.active,
            sub_category_id:this.newAssets.sub_category_id,
            initial_book_value:this.newAssets.initial_book_value,
            effective_date:this.newAssets.effective_date,
            is_depreciable:this.newAssets.is_depreciable,
            is_internal:this.newAssets.is_internal,
            is_new:this.newAssets.is_new,
            is_rented:this.newAssets.is_rented,
            balance:this.newAssets.initial_book_value,
            type_id:this.newAssets.type_id,
            depreciation_tipe_id:this.newAssets.depreciation_tipe_id,
            warranty_expiration:this.newAssets.warranty_expiration
        };
        let request = null;
        if (this.isNewAsset == true) {
          request = await this.api_Post("inventory/assets", assetData);
        } else {
          request = await this.api_Put("inventory/assets", assetData);
        }
        const data = await request.json();
        if (request.status == 200) {
          this.filterAssets();
          this.$toast.success("Registro realizado con exito!");
          this.page = 1;
        } else {
          this.$toast.error(data.error);
         // await this.delete_file_storage("fixed_assets", newImage.id);
        }
        this.loading_save=false;
      } catch (error) {
        this.$toast.error(error.message);
        console.error(error);
        this.loading_save=false;
      }
    },
    editAsset(item) {
      this.newAssets = { ...item };
      this.assetsImage = this.assetImageUrl(item.image_url);
      this.changeImage = false;
      this.url_image = null;
      this.page = 2;
      this.isNewAsset = false;
    },
    async viewAsset(item) {
      this.viewAssetData = { ...item };
      this.viewAssetImage = this.assetImageUrl(item.image_url);
      this.history = { depreciation: [], transfers: [], maintenance: [], attachments: [], disposals: [] };
      this.dialogView = true;
      await this.loadAssetHistory(item.code);
    },
    async loadAssetHistory(code) {
      try {
        const [dep, tr, mt, at, dp] = await Promise.all([
          this.api_Get(`inventory/asset_depreciation/${code}`),
          this.api_Get(`inventory/asset_transfers/${code}`),
          this.api_Get(`inventory/asset_maintenance/${code}`),
          this.api_Get(`inventory/asset_attachments/${code}`),
          this.api_Get(`inventory/asset_disposals/${code}`),
        ]);
        this.history.depreciation = dep.ok ? await dep.json() : [];
        this.history.transfers   = tr.ok ? await tr.json() : [];
        this.history.maintenance = mt.ok ? await mt.json() : [];
        this.history.attachments = at.ok ? await at.json() : [];
        this.history.disposals   = dp.ok ? await dp.json() : [];
      } catch (e) {
        console.error('[assets] loadAssetHistory:', e);
      }
    },
    goEditFromView() {
      this.dialogView = false;
      this.editAsset(this.viewAssetData);
    },
    attachmentIcon(fileType) {
      const map = {
        factura: 'receipt_long',
        garantia: 'verified',
        manual: 'menu_book',
        poliza: 'policy',
        foto: 'image',
        otro: 'description',
      };
      return map[fileType] || 'description';
    },
    disposalLabel(t) {
      const map = { sale: 'Venta', donation: 'Donación', obsolete: 'Obsoleto', loss: 'Pérdida', theft: 'Robo' };
      return map[t] || t;
    },
    // --------- DAR DE BAJA ----------
    openDialogDisposal() {
      this.disposalForm = {
        disposal_type: 'sale',
        disposal_date: new Date().toISOString().substr(0, 10),
        disposal_amount: 0,
        reason: '',
        dateMenu: false,
      };
      this.dialogDisposal = true;
    },
    async submitDisposal() {
      if (!this.disposalForm.disposal_type) {
        this.$toast.warning('Indique el motivo de baja'); return;
      }
      this.loadingAction = true;
      try {
        const r = await this.api_Post('inventory/asset_disposals', {
          asset_code: this.viewAssetData.code,
          disposal_date: this.disposalForm.disposal_date,
          disposal_type: this.disposalForm.disposal_type,
          disposal_amount: this.disposalForm.disposal_amount,
          reason: this.disposalForm.reason,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        if (r.status === 200) {
          this.$toast.success('Activo dado de baja');
          this.dialogDisposal = false;
          this.dialogView = false;
          this.filterAssets();
        } else {
          const d = await r.json();
          this.$toast.error(d.error || 'Error al dar de baja');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    // --------- TRANSFERIR ----------
    openDialogTransfer() {
      this.transferForm = {
        transfer_date: new Date().toISOString().substr(0, 10),
        to_business_unit_id: this.viewAssetData.business_unit_id,
        to_location_id: this.viewAssetData.location_id,
        to_department_id: this.viewAssetData.department_id,
        reason: '',
        dateMenu: false,
      };
      this.dialogTransfer = true;
    },
    async submitTransfer() {
      if (!this.transferForm.to_business_unit_id || !this.transferForm.to_location_id) {
        this.$toast.warning('Indique destino'); return;
      }
      this.loadingAction = true;
      try {
        const r = await this.api_Post('inventory/asset_transfers', {
          asset_code: this.viewAssetData.code,
          transfer_date: this.transferForm.transfer_date,
          to_business_unit_id: this.transferForm.to_business_unit_id,
          to_location_id: this.transferForm.to_location_id,
          to_department_id: this.transferForm.to_department_id,
          reason: this.transferForm.reason,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        if (r.status === 200) {
          this.$toast.success('Transferencia registrada');
          this.dialogTransfer = false;
          await this.loadAssetHistory(this.viewAssetData.code);
          this.filterAssets();
        } else {
          const d = await r.json();
          this.$toast.error(d.error || 'Error al transferir');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    // --------- MANTENIMIENTO ----------
    openDialogMaintenance() {
      this.maintenanceForm = {
        maintenance_type: 'preventive',
        maintenance_date: new Date().toISOString().substr(0, 10),
        description: '',
        provider_name: '',
        cost: 0,
        next_maintenance_date: null,
        dateMenu: false,
        nextMenu: false,
      };
      this.dialogMaintenance = true;
    },
    async submitMaintenance() {
      if (!this.maintenanceForm.description) {
        this.$toast.warning('Indique la descripción'); return;
      }
      this.loadingAction = true;
      try {
        const r = await this.api_Post('inventory/asset_maintenance', {
          asset_code: this.viewAssetData.code,
          maintenance_date: this.maintenanceForm.maintenance_date,
          maintenance_type: this.maintenanceForm.maintenance_type,
          description: this.maintenanceForm.description,
          provider_name: this.maintenanceForm.provider_name,
          cost: this.maintenanceForm.cost,
          next_maintenance_date: this.maintenanceForm.next_maintenance_date,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        if (r.status === 200) {
          this.$toast.success('Mantenimiento registrado');
          this.dialogMaintenance = false;
          await this.loadAssetHistory(this.viewAssetData.code);
        } else {
          const d = await r.json();
          this.$toast.error(d.error || 'Error al guardar mantenimiento');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    // --------- ADJUNTOS ----------
    openDialogAttachment() {
      this.attachmentForm = { name: '', description: '', file_type: 'otro', file: null };
      this.dialogAttachment = true;
    },
    async submitAttachment() {
      if (!this.attachmentForm.file) {
        this.$toast.warning('Seleccione un archivo'); return;
      }
      if (!this.attachmentForm.name) {
        this.attachmentForm.name = this.attachmentForm.file.name;
      }
      this.loadingAction = true;
      try {
        const up = await this.upload_to_spaces(this.attachmentForm.file, 'asset_attachments');
        if (!up || !up.key) throw new Error('Upload sin respuesta');
        const r = await this.api_Post('inventory/asset_attachments', {
          asset_code: this.viewAssetData.code,
          name: this.attachmentForm.name,
          description: this.attachmentForm.description,
          url: up.key,
          file_type: this.attachmentForm.file_type,
          file_size: this.attachmentForm.file.size,
          mime_type: this.attachmentForm.file.type,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        if (r.status === 200) {
          this.$toast.success('Adjunto cargado');
          this.dialogAttachment = false;
          await this.loadAssetHistory(this.viewAssetData.code);
        } else {
          const d = await r.json();
          this.$toast.error(d.error || 'Error al guardar');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    async deleteAttachment(id) {
      if (!confirm('¿Eliminar este adjunto?')) return;
      try {
        const r = await this.api_Delete(`inventory/asset_attachments/${id}`);
        if (r.ok) {
          this.$toast.success('Adjunto eliminado');
          await this.loadAssetHistory(this.viewAssetData.code);
        }
      } catch (e) { this.$toast.error(e.message); }
    },
    // --------- DEPRECIACIÓN MENSUAL ----------
    openDialogDepRun() {
      this.depRunForm = { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
      this.dialogDepRun = true;
    },
    async runDepreciation() {
      this.loadingAction = true;
      try {
        const r = await this.api_Post('inventory/asset_depreciation/run', {
          year: this.depRunForm.year,
          month: this.depRunForm.month,
          business_unit_id: this.business_selected,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        const d = await r.json();
        if (r.status === 200) {
          this.$toast.success(`Depreciación corrida: ${d.processed} activos · Total ${d.total_depreciation}`);
          this.dialogDepRun = false;
          this.filterAssets();
        } else {
          this.$toast.error(d.error || 'Error al correr depreciación');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    // --------- EXCEL EXPORT ----------
    async exportAssetsExcel() {
      if (!this.assets.length) {
        this.$toast.info('No hay datos para exportar'); return;
      }
      try {
        const ExcelJS = (await import('exceljs')).default;
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Activos Fijos');
        ws.columns = [
          { header: 'Código', key: 'code', width: 15 },
          { header: 'Activo', key: 'item_name', width: 40 },
          { header: 'Categoría', key: 'category', width: 18 },
          { header: 'Sub categoría', key: 'sub_category', width: 18 },
          { header: 'Modelo', key: 'model', width: 15 },
          { header: 'Serial', key: 'serial_number', width: 15 },
          { header: 'Unidad de negocio', key: 'business_unit_name', width: 22 },
          { header: 'Location', key: 'location_name', width: 22 },
          { header: 'Departamento', key: 'department_name', width: 20 },
          { header: 'Responsable', key: 'entity_name', width: 25 },
          { header: 'Fecha compra', key: 'date_of_purchase', width: 14 },
          { header: 'Precio compra', key: 'amount', width: 16, style: { numFmt: '#,##0.00' } },
          { header: 'Valor inicial', key: 'initial_book_value', width: 16, style: { numFmt: '#,##0.00' } },
          { header: 'Valor actual', key: 'balance', width: 16, style: { numFmt: '#,##0.00' } },
          { header: 'Salvamento', key: 'salvage_value', width: 14, style: { numFmt: '#,##0.00' } },
          { header: 'Vida útil (años)', key: 'useful_life', width: 14 },
          { header: 'Fecha inicio depr.', key: 'effective_date', width: 16 },
          { header: 'Garantía vence', key: 'warranty_expiration', width: 16 },
          { header: 'Depreciable', key: 'is_depreciable', width: 12 },
          { header: 'Activo', key: 'active', width: 10 },
        ];
        this.assets.forEach(a => ws.addRow({
          ...a,
          date_of_purchase: a.date_of_purchase ? moment(a.date_of_purchase).format('DD/MM/YYYY') : '',
          effective_date: a.effective_date ? moment(a.effective_date).format('DD/MM/YYYY') : '',
          warranty_expiration: a.warranty_expiration ? moment(a.warranty_expiration).format('DD/MM/YYYY') : '',
          is_depreciable: a.is_depreciable ? 'Sí' : 'No',
          active: a.active ? 'Sí' : 'No',
        }));
        // Estilo header
        ws.getRow(1).eachCell(c => {
          c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
          c.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        ws.views = [{ state: 'frozen', ySplit: 1 }];
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `activos_fijos_${moment().format('YYYYMMDD_HHmm')}.xlsx`;
        link.click();
        this.$toast.success('Excel generado');
      } catch (e) {
        this.$toast.error('Error al exportar: ' + e.message);
      }
    },
    // --------- ALERTAS ----------
    warrantyAlert(item) {
      if (!item.warranty_expiration) return false;
      const diff = moment(item.warranty_expiration).diff(moment(), 'days');
      return diff >= 0 && diff <= 30;
    },
    // --------- TOGGLE ACTIVE ----------
    async toggleActive(newValue) {
      this.loadingAction = true;
      try {
        const r = await this.api_Patch('inventory/assetActive', {
          code: this.viewAssetData.code,
          active: newValue,
          created_by: this.$store.state.auth.user.user.user_id,
        });
        if (r.ok) {
          this.$toast.success(newValue ? 'Activo reactivado' : 'Activo inactivado');
          this.filterAssets();
        } else {
          this.viewAssetData.active = !newValue;
          this.$toast.error('No se pudo cambiar el estado');
        }
      } catch (e) {
        this.viewAssetData.active = !newValue;
        this.$toast.error(e.message);
      } finally {
        this.loadingAction = false;
      }
    },
    // --------- IMPRIMIR ETIQUETA QR ----------
    printAssetLabel() {
      const a = this.viewAssetData;
      const qrData = encodeURIComponent(JSON.stringify({
        code: a.code,
        name: a.item_name,
        serial: a.serial_number,
      }));
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${qrData}`;
      // Etiqueta 4"x2" (100mm x 50mm)
      const doc = new jsPDF({ orientation: 'l', unit: 'mm', format: [100, 50] });
      // Marco
      doc.setDrawColor(100); doc.setLineWidth(0.3);
      doc.rect(2, 2, 96, 46);
      // QR
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = qrUrl;
      img.onload = () => {
        doc.addImage(img, 'PNG', 4, 4, 42, 42);
        // Info
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(a.code || '', 50, 12);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text((a.item_name || '').substring(0, 40), 50, 18);
        doc.setFontSize(7);
        doc.text(`Modelo: ${a.model || '—'}`, 50, 24);
        doc.text(`Serial: ${a.serial_number || '—'}`, 50, 28);
        doc.text(`Ubicación: ${(a.location_name || '').substring(0, 28)}`, 50, 32);
        doc.text(`Resp.: ${(a.entity_name || '').substring(0, 28)}`, 50, 36);
        doc.setFontSize(6);
        doc.setTextColor(120);
        doc.text(`Gen: ${moment().format('DD/MM/YYYY')}`, 50, 44);
        doc.save(`etiqueta_${a.code}.pdf`);
      };
      img.onerror = () => {
        this.$toast.error('No se pudo cargar el QR. Verifica conexión a internet.');
      };
    },
    depreciationPreview(asset) {
      const ibv = parseFloat(asset.initial_book_value) || 0;
      const sv = parseFloat(asset.salvage_value) || 0;
      const ul = parseFloat(asset.useful_life) || 0;
      if (!ul) return 0;
      return (ibv - sv) / ul;
    },
    fmtDate(d) {
      if (!d) return '—';
      return moment(d).format('DD/MM/YYYY');
    },
    assetImageUrl(image_url) {
      if (!image_url) return null;
      // String key de DO Spaces (ej: "fixed_assets/uuid.png" o "assets/uuid.jpg")
      if (typeof image_url === 'string') {
        if (image_url.startsWith('http')) return image_url;
        return this.spaces_file_url(image_url);
      }
      // Objeto (PocketBase antiguo u objeto con {url, key})
      if (image_url.url) return image_url.url;
      if (image_url.key) return this.spaces_file_url(image_url.key);
      return this.get_storage(image_url);
    },
    async getImage(imageId) {
      if (imageId == null || imageId == "" || imageId == undefined) {
        this.assetsImage = null;
        this.newAssets.image_url = null;
        return;
      }
      this.assetsImage = await this.get_storage(imageId);
    },
    set_employee(employee) {
      this.newAssets.entity_id = employee.entity_id;
    },
    clear_employee() {
      this.newAssets.newAssets = null;
    },
    set_user(user) {
      this.newAssets.assigned_user_id = user[0].use_id;
    },
    clear_user() {
      this.newAssets.assigned_user_id = null;
    },
    set_invoice(invoice) {
      if (!invoice.length) return;

      const [firstInvoice] = invoice;
      const { invoice_id, account_date } = firstInvoice;

      const item = invoice.find(element => element.item_id === this.newAssets.item_id);

      if (this.newAssets.item_id && !item) {
        this.$toast.error(`El activo ${this.newAssets.item_name} no se encuentra en esta factura!`);
        return;
      }

      if (item) {
        this.newAssets.amount = item.price;
      } else if (!this.newAssets.item_id && invoice.length === 1) {
        const { item_description, item_id, price } = firstInvoice;
        Object.assign(this.newAssets, {
          item_name: item_description,
          item_id,
          amount: price
        });
      }

      Object.assign(this.newAssets, {
        invoice_id,
        date_of_purchase: account_date,
        invoice_items: invoice
      });
    },
    clear_invoice() {
      this.newAssets.invoice_id=null;
      this.newAssets.date_of_purchase=null;
      this.newAssets.invoice_items=[];
      this.newAssets.amount=null;
    },
    async depreciation_assets_pdf() {
      let assets_data=[];
        try {
          const request = await this.api_Post("inventory/depreciation_assets", {
            business_unit_id: this.business_selected,
            location_id: this.location_selected,
            department_id: this.filters.department_id,
            asset_code: this.clearInputFilter(this.filters.asset_code, 0)
          });
          
          const data = await request.json();
          
          if (request.status != 200) {
            this.$toast.error(data.error);
            this.assets = data;
            return;
          }

          const currentDate = moment(); // Fecha actual
          const currentDayOfMonth = currentDate.date(); // Día actual del mes

          for (let asset of data) {
            const { initial_book_value, depreciation_percentage, effective_date, useful_life } = asset;
            const startDate = moment(effective_date);
            let book_value=parseFloat(initial_book_value);
        
            let anual_depreciation = book_value * parseFloat(depreciation_percentage);
            let monthly_depreciation = anual_depreciation / 12;
            let daily_depreciation = monthly_depreciation / currentDate.daysInMonth(); // Depreciación diaria
            const total_months = parseInt(useful_life) * 12; // Vida útil en meses
            let depreciation_accumulated = 0;

            // Crear objeto para almacenar la depreciación mensual
            const monthlyDepreciationData = {};
              let year = parseInt(startDate.format('YYYY'))
            for (let i = 0; i < total_months; i++) {
              const currentMonth = startDate.clone().add(i, 'months');
              //VA REDUCIENDO EL VALOR EN LIBRO CADA VEZ QUE SUBE UN ANIO\      

              if(parseInt(currentMonth.format('YYYY'))>year){
                year=parseInt(currentMonth.format('YYYY'));
                  book_value= book_value - depreciation_accumulated;
                  anual_depreciation = parseFloat(book_value) * parseFloat(depreciation_percentage);
                  monthly_depreciation = anual_depreciation / 12;
                  daily_depreciation = monthly_depreciation / currentDate.daysInMonth(); // Depreciación diaria
              }

              if (currentMonth.isAfter(currentDate)) break; // Detener si supera la fecha actual

              // Si es el mes actual, calcula la depreciación hasta el día actual
              if (currentMonth.isSame(currentDate, 'month')) {
                const partial_month_depreciation = daily_depreciation * currentDayOfMonth;
                depreciation_accumulated += partial_month_depreciation;
                if(year==parseInt(moment().format('YYYY'))){
                  monthlyDepreciationData[currentMonth.format('MMM')] = {
                    depreciation: partial_month_depreciation.toFixed(2),
                    accumulated: depreciation_accumulated.toFixed(2)
                  };
                }
              } else {
                // Depreciación completa para meses pasados
                depreciation_accumulated += monthly_depreciation;
                if(year==parseInt(moment().format('YYYY'))){
                  monthlyDepreciationData[currentMonth.format('MMM')] = {
                    depreciation: monthly_depreciation.toFixed(2),
                    accumulated: depreciation_accumulated.toFixed(2)
                  };
                }
              }
            }

            assets_data.push({
              code: asset.code,
              name: asset.name,
              effective_date: asset.effective_date,
              initial_book_value:book_value,
              anual_depreciation: anual_depreciation.toFixed(2),
              depreciation_accumulated: depreciation_accumulated.toFixed(2),
              monthlyDepreciationData
            });

            // Aquí podrías organizar los datos para exportarlos al formato del reporte.
          }
          
        } catch (error) {
          this.$toast.error(error.message);
        }
        if(assets_data.length==0){
            this.$toast.info('No se encontraron datos!');
            return
          }
        this.generateDepreciationPDF(assets_data)
    },
    generateDepreciationPDF(data) {
      const doc = new jsPDF("l", "mm", [476, 279]);
      const business = this.$store.state.auth.bussines_units.find((element)=>element.value==this.business_selected);
   
      // Encabezado principal
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(business?.text, 215, 15);

      let yPosition = 40;
      doc.setFontSize(12);
      doc.text(`Fecha: ${moment().format('DD/MM/YYYY hh:mm a')}`, 420, 15);
      doc.text(`Pagina 1`, 420, 20);
      
      doc.text(`Desde: ${moment().format('DD/MM/YYYY')} hasta: ${moment().format('DD/MM/YYYY')}`, 14, yPosition-10);
      doc.text("ANALISIS MENSUAL DE ACTIVOS FIJOS", 215, yPosition-10);
      doc.setFontSize(8);
      // Encabezados de la tabla
      doc.line(14, yPosition-5, 465, yPosition-5);
      doc.text("CODIGO", 14, yPosition);
      doc.text("DESCRIPCION", 35, yPosition);
      doc.text("FECHA", 95, yPosition);
      doc.text("COSTO", 115, yPosition);
      doc.text("VAL. LIBRO", 135, yPosition);
      doc.text("DEP. ACUM.", 165, yPosition);
      doc.text("DEP. ANUAL", 190, yPosition);
      doc.text("DEP. DEL AÑO", 215, yPosition);
      doc.line(14, yPosition+3, 465, yPosition+3);
      // Encabezados de meses
      const months = [{value:"ene.", text:"ENE"}, {value:"feb.", text:"FEB"}, {value:"mar.", text:"MAR"}, {value:"abr.", text:"ABR"}, {value:"may.", text:"MAY"}, {value:"jun.", text:"JUN"}, {value:"jul.", text:"JUL"}, {value:"ago.", text:"AGO"}, {value:"sep.", text:"SEP"}, {value:"oct.", text:"OCT"}, {value:"nov.", text:"NOV"}, {value:"dic", text:"DIC"}, {value:"", text:"D. ACU"}];
      let xPosition = 250;

      for (const month of months) {
        doc.text(month.text, xPosition, yPosition);
        xPosition += 17;
      }
      yPosition += 8;

      yPosition += 4;

      // Contenido de la tabla
      doc.setFont("helvetica", "normal");
      for (let asset of data) {
      const {
        code,
        name,
        effective_date,
        initial_book_value,
        anual_depreciation,
        depreciation_accumulated,
        monthlyDepreciationData = {}
      } = asset;

      // Imprimir información básica del activo
      doc.text(code, 14, yPosition);
      doc.text(`${name.slice(0,30)}`, 35, yPosition);
      doc.text(`${moment(effective_date).format('DD/MM/YYYY')}`, 93, yPosition); // Ajusta posiciones según el ancho
      doc.text(`${this.filLeft(this.currencyFormat(initial_book_value),13,' ')}`, 111, yPosition);
      doc.text(`${this.filLeft(this.currencyFormat(initial_book_value),13,' ')}`, 133, yPosition);
      doc.text(`${this.filLeft(this.currencyFormat(depreciation_accumulated),13,' ')}`, 161, yPosition);
      doc.text(`${this.filLeft(this.currencyFormat(anual_depreciation),12,' ')}`, 190, yPosition);
      // Iterar sobre los datos de depreciación mensual
      let xPosition = 247;

      for (let month of months) {
        if(month.value==""){
          continue
        }
        const depreciation=monthlyDepreciationData[month.value]?.depreciation;
        doc.text(`${this.filLeft(depreciation !=undefined? this.currencyFormat(depreciation) : '0.00',10,' ')}`, xPosition, yPosition);
        xPosition += 17; 
      }
      doc.text(this.filLeft(' - ',540,' - '), 14, yPosition+5)
      yPosition += 12;

      // Salto de página si es necesario
      if (yPosition > 190) { // Ajustado para landscape
        doc.addPage();
        yPosition = 20;
      }
    }
      // Total y final del reporte
      yPosition += 10;
      doc.setFont("helvetica", "bold");
      doc.text(`TOTAL ACTIVO: ${data.length}`, 14, yPosition);
      yPosition += 10;
      doc.text("FIN DEL REPORTE.........................................................", 14, yPosition);

      // Guardar o mostrar el PDF
      doc.save("Analisis_Mensual_Activos_Fijos.pdf");
    },
  },
};
</script>
<style scoped>
:deep(.page-wrapper) {
  all: unset;
}

.assets-container {
  background-color: #f5f7fa;
  min-height: calc(100vh - 64px);
}

.transparent-stepper {
  background: transparent !important;
  box-shadow: none !important;
}

/* KPI Cards */
.kpi-card {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  border-radius: 8px !important;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
}

/* Tabla */
.assets-table :deep(thead tr th) {
  background-color: #fafbfc !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.6px;
  color: #546e7a !important;
  border-bottom: 2px solid #e0e0e0 !important;
}
.assets-table :deep(tbody tr:hover) {
  background-color: #f5f9ff !important;
}
.assets-table :deep(tbody tr td) {
  border-bottom: 1px solid #eceff1 !important;
}

/* Section labels inside tabs */
.section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #78909c;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eceff1;
}

/* Tabs */
:deep(.v-tab) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500;
  font-size: 14px;
}

/* Cards */
:deep(.v-card.v-sheet--outlined) {
  border-radius: 8px !important;
  border-color: #e0e4e8 !important;
}

/* Visor de activo */
.view-dialog {
  border-radius: 10px !important;
  overflow: hidden;
}
.view-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}
.info-field {
  padding: 6px 10px;
  background: #fafbfc;
  border-radius: 6px;
  border: 1px solid #eceff1;
  min-height: 48px;
}
.info-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #90a4ae;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.info-value {
  font-size: 13px;
  color: #263238;
  line-height: 1.4;
}
</style>